import { visit, SKIP } from 'unist-util-visit';
import type { Root, Element, Text, ElementContent } from 'hast';
import { findTermMatches } from './glossaryMatch';

// A rehype plugin that turns the FIRST occurrence (per glossary slug) of each
// known technical term, found in plain-text nodes, into a custom HAST element:
//
//     <glossary-term data-slug="…" data-text="…">surface text</glossary-term>
//
// The Markdown renderer maps that element name to the interactive <GlossaryTerm>
// React component via react-markdown's `components` prop.
//
// It only touches Text nodes and deliberately skips text inside <code>, <pre>,
// <a> (already a link), and KaTeX output (.math / .katex), so bold, lists,
// links, code, and rendered formulas are never disturbed.

const SKIP_TAGS = new Set(['code', 'pre', 'a', 'script', 'style']);

function isSkippedElement(node: Element): boolean {
  if (SKIP_TAGS.has(node.tagName)) return true;
  const cls = node.properties?.className;
  const classes = Array.isArray(cls) ? cls.map(String) : [];
  // KaTeX renders into elements carrying these classes; never rewrite inside them.
  return classes.some((c) => c === 'math' || c === 'katex' || c.startsWith('katex'));
}

export function rehypeGlossary() {
  return (tree: Root) => {
    // Track which slugs we've already wrapped so each appears at most once
    // across the whole rendered block (not just per text node).
    const usedSlugs = new Set<string>();

    visit(tree, (node, index, parent) => {
      // Don't descend into skipped subtrees (code/links/katex/etc.).
      if (node.type === 'element' && isSkippedElement(node as Element)) {
        return SKIP;
      }

      if (
        node.type !== 'text' ||
        parent == null ||
        index == null ||
        !('children' in parent)
      ) {
        return;
      }

      const textNode = node as Text;
      const value = textNode.value;
      if (!value.trim()) return;

      // Find candidate matches (first-per-slug within THIS text node), then
      // filter against slugs already used elsewhere in the tree.
      const matches = findTermMatches(value, true).filter(
        (m) => !usedSlugs.has(m.term.slug),
      );
      if (matches.length === 0) return;

      // Rebuild this text node as a sequence of [text, <glossary-term>, text…].
      const replacement: ElementContent[] = [];
      let cursor = 0;
      for (const match of matches) {
        if (usedSlugs.has(match.term.slug)) continue;
        usedSlugs.add(match.term.slug);
        if (match.start > cursor) {
          replacement.push({ type: 'text', value: value.slice(cursor, match.start) });
        }
        replacement.push({
          type: 'element',
          tagName: 'glossary-term',
          properties: { dataSlug: match.term.slug, dataText: match.text },
          children: [{ type: 'text', value: match.text }],
        });
        cursor = match.end;
      }
      if (cursor < value.length) {
        replacement.push({ type: 'text', value: value.slice(cursor) });
      }

      const siblings = (parent as Element | Root).children;
      siblings.splice(index, 1, ...replacement);
      // Skip the nodes we just inserted (advance past them).
      return [SKIP, index + replacement.length];
    });
  };
}
